type Variant = "compact" | "full";

const IOS_URL =
  "https://apps.apple.com/us/app/a-dark-place/id1615602506";
const ANDROID_URL =
  "https://play.google.com/store/apps/details?id=com.olsiodobashi.adp&pcampaignid=web_share";

const AppleIcon = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    fill="currentColor"
  >
    <path d="M16.365 1.43c0 1.14-.43 2.23-1.21 3.06-.83.92-2.21 1.63-3.36 1.54-.13-1.13.45-2.31 1.18-3.07.83-.86 2.23-1.5 3.39-1.53zm3.34 18.49c-.6 1.4-.9 2.02-1.66 3.26-1.07 1.74-2.58 3.9-4.45 3.91-1.66.02-2.09-1.08-4.34-1.07-2.25.01-2.72 1.09-4.39 1.07-1.87-.02-3.3-1.97-4.36-3.71C-.74 19.5-1.27 14.78.57 12.05c1.31-1.95 3.38-3.09 5.32-3.09 1.98 0 3.23 1.08 4.86 1.08 1.59 0 2.55-1.08 4.85-1.08 1.74 0 3.59.95 4.91 2.59-4.32 2.36-3.62 8.55.2 10.32z" />
  </svg>
);

const PlayIcon = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 512 512"
    aria-hidden="true"
    className={className}
  >
    {/* Stylized play triangle in accent crimson — matches the dark theme */}
    <defs>
      <linearGradient id="adp-play" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ef4444" />
        <stop offset="100%" stopColor="#b91c1c" />
      </linearGradient>
    </defs>
    <path
      fill="url(#adp-play)"
      d="M73 32c-9 5-15 14-15 26v404c0 12 6 21 15 26l232-228L73 32zm270 196l64-37c14-8 14-28 0-36l-64-37-72 70 72 40zm-30 16L94 466c8 4 18 4 27-1l232-134-40-87z"
    />
  </svg>
);

const Badge = ({
  href,
  superscript,
  title,
  icon,
}: {
  href: string;
  superscript: string;
  title: string;
  icon: React.ReactNode;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer noopener"
    className="group flex items-center gap-3 px-4 py-2.5 rounded-lg border border-ink-600 hover:border-accent-500/70 bg-ink-900/80 hover:bg-ink-800 transition-colors min-w-[160px]"
  >
    <span className="text-ink-100 w-6 h-6 flex-shrink-0">{icon}</span>
    <span className="flex flex-col leading-tight">
      <span className="text-[10px] uppercase tracking-[0.2em] text-ink-400 group-hover:text-ink-300">
        {superscript}
      </span>
      <span className="text-sm text-white font-medium">{title}</span>
    </span>
  </a>
);

const AppStoreLinks = ({
  variant = "compact",
  className = "",
}: {
  variant?: Variant;
  className?: string;
}) => {
  if (variant === "full") {
    return (
      <section
        className={`mt-16 rounded-xl border border-ink-700 bg-ink-900/60 px-6 py-10 text-center shadow-glow ${className}`}
      >
        <p className="text-xs uppercase tracking-[0.4em] text-accent-400 mb-2">
          Take it with you
        </p>
        <h2 className="font-display text-3xl text-white">
          Read on iPhone or Android
        </h2>
        <p className="text-ink-400 mt-2 max-w-md mx-auto text-sm">
          The same stories, formatted for the dark — wherever you read them.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Badge
            href={IOS_URL}
            icon={<AppleIcon className="w-6 h-6" />}
            superscript="Download on the"
            title="App Store"
          />
          <Badge
            href={ANDROID_URL}
            icon={<PlayIcon className="w-6 h-6" />}
            superscript="Get it on"
            title="Google Play"
          />
        </div>
      </section>
    );
  }

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <Badge
        href={IOS_URL}
        icon={<AppleIcon className="w-5 h-5" />}
        superscript="Download on the"
        title="App Store"
      />
      <Badge
        href={ANDROID_URL}
        icon={<PlayIcon className="w-5 h-5" />}
        superscript="Get it on"
        title="Google Play"
      />
    </div>
  );
};

export default AppStoreLinks;
